import { dateService } from '@/core/services/date'
import { AirportModel } from '@/features/locations/types/models/airport.model'
import { CityModel } from '@/features/locations/types/models/city.model'
import { CountryModel } from '@/features/locations/types/models/country.model'
import { MIN_HOURS_BEFORE_TRIP_UPDATE } from '@/features/trips/constants'
import { TripDetailsModel } from '@/features/trips/types/models/trip-details.model'
import { create } from 'zustand'
import { BookFormModel } from '../types/models/book-form.model'
import { CreateTripModel } from '../types/models/create-trip.model'
import { CreatedTripModel } from '../types/models/created-trip.model'
import { PaymentDetailsModel } from '../types/models/payment-details.model'
import { TripInfoModel } from '../types/models/trip-info.model'

type ReservationState = {
  country: CountryModel | null
  city: CityModel | null
  bookForm: BookFormModel
  tripInfo: TripInfoModel | null
  createdTrip: CreatedTripModel | null
  tripToUpdate: TripDetailsModel | null
  paymentDetails: PaymentDetailsModel | null
  promoCodePrice: number
  createTrip: CreateTripModel | null
  selectedLocations: Record<'from' | 'to', AirportModel | null>
}

type ReservationActions = {
  setCountry: (country: CountryModel) => void
  setCity: (city: CityModel) => void
  setBookForm: (bookForm: BookFormModel) => void
  setTripInfo: (tripInfo: TripInfoModel) => void
  setCreatedTrip: (createdTrip: CreatedTripModel) => void
  setTripToUpdate: (tripToUpdate: TripDetailsModel) => void
  setPaymentDetails: (paymentDetails: PaymentDetailsModel | null) => void
  setPromoCodePrice: (promoCodePrice: number) => void
  reset: () => void
  setCreateTrip: (createTrip: CreateTripModel) => void
  updateCreateTrip: (createTrip: Partial<CreateTripModel>) => void
  setSelectedLocation: (
    field: 'from' | 'to',
    location: AirportModel | null,
  ) => void
}

export const bookFormInitialState: BookFormModel = {
  date: dateService.addHours(new Date(), MIN_HOURS_BEFORE_TRIP_UPDATE),
  time: dateService.roundMinutes(
    dateService.addHours(new Date(), MIN_HOURS_BEFORE_TRIP_UPDATE),
  ),
  passengers: 1,
  checkedLuggage: 0,
  handLuggage: 0,
  addChildSeats: false,
  carrier: 0,
  childSeats: 0,
  booster: 0,
  countryId: '',
  fromPoint: '',
  fromPointLat: '',
  fromPointLong: '',
  fromPointName: '',
  fromPointTypes: '',
  toPoint: '',
  toPointLat: '',
  toPointLong: '',
  toPointName: '',
  toPointTypes: '',
}

const initialState: ReservationState = {
  country: null,
  city: null,
  bookForm: bookFormInitialState,
  tripInfo: null,
  createdTrip: null,
  tripToUpdate: null,
  paymentDetails: null,
  promoCodePrice: 0,
  createTrip: null,
  selectedLocations: {
    from: null,
    to: null,
  },
}

export const useReservationStore = create<
  ReservationState & ReservationActions
>((set, get) => ({
  ...initialState,
  setTripInfo(tripInfo) {
    set({ tripInfo })
  },
  setBookForm(bookForm) {
    set({ bookForm })
  },
  setCountry(country) {
    set({ country })
  },
  setCity(city) {
    set({ city })
  },
  setCreatedTrip(createdTrip) {
    set({ createdTrip })
  },
  setTripToUpdate(tripToUpdate) {
    set({ tripToUpdate })
  },
  setPaymentDetails(paymentDetails) {
    set({ paymentDetails })
  },
  setPromoCodePrice(promoCodePrice) {
    set({ promoCodePrice })
  },
  reset() {
    set(initialState)
  },
  setCreateTrip(createTrip) {
    set({ createTrip })
  },
  updateCreateTrip(createTrip) {
    const updated = Object.fromEntries(
      Object.entries({ ...get().createTrip, ...createTrip }).filter(([, v]) =>
        Boolean(v),
      ),
    ) as CreateTripModel
    set({ createTrip: updated })
  },
  setSelectedLocation(field, location) {
    set({
      selectedLocations: { ...get().selectedLocations, [field]: location },
    })
  },
}))
